import { useState, useMemo, useCallback } from 'react';
import PhotoLightbox, { type LightboxPhoto } from './PhotoLightbox';
import { coVisibilityBadgeLabel, coVisibilityFromAttachment } from '@/lib/changeOrderUtils';
import {
  FILE_OPEN_ERROR_MESSAGE,
  openProjectFileInNewTab,
  resolveFileDisplayUrl,
  useSignedFileUrls,
} from '@/lib/storageSignedUrl';

export interface InstructionAttachment {
  id: string;
  file_url: string;
  file_name: string;
  file_type: string | null;
  photo_type: string | null;
  caption: string | null;
  note?: string | null;
  file_size?: string | number | null;
  is_client_visible?: boolean | null;
  is_subcontractor_visible?: boolean | null;
  change_order_id?: string | null;
  created_at?: string | null;
  storage_bucket?: string | null;
  storage_path?: string | null;
}

interface Props {
  files: InstructionAttachment[];
  maxVisible?: number;
  className?: string;
}

const PHOTO_TYPE_LABELS: Record<string, string> = {
  before: 'Before',
  after: 'After',
  reference: 'Reference',
  attachment: 'Attachment',
  current: 'Current',
  new: 'New',
};

function getPhotoTypeLabel(photoType: string | null | undefined): string | undefined {
  if (!photoType) return undefined;
  if (photoType.startsWith('co_vis_')) {
    return coVisibilityBadgeLabel(coVisibilityFromAttachment({ photo_type: photoType }));
  }
  return PHOTO_TYPE_LABELS[photoType] || photoType;
}

function getChangeOrderVisibilityBadge(file: InstructionAttachment): string | undefined {
  if (file.photo_type === 'co_invoice') return undefined;
  if (file.photo_type?.startsWith('co_vis_') || file.change_order_id) {
    return coVisibilityBadgeLabel(coVisibilityFromAttachment(file));
  }
  return undefined;
}

function isImage(fileType: string | null): boolean {
  if (!fileType) return false;
  const t = fileType.toLowerCase();
  return ['jpg', 'jpeg', 'png', 'webp', 'heic', 'gif', 'bmp'].includes(t);
}

function isPdf(fileType: string | null, fileName?: string): boolean {
  if (fileType?.toLowerCase() === 'pdf') return true;
  return fileName?.toLowerCase().endsWith('.pdf') ?? false;
}

export default function InstructionAttachmentThumbnails({
  files,
  maxVisible = 3,
  className = '',
}: Props) {
  const [lightboxOpen, setLightboxOpen] = useState(false);
  const [lightboxIndex, setLightboxIndex] = useState(0);
  const [openError, setOpenError] = useState('');

  const fileUrls = useSignedFileUrls(files);

  const openAttachment = useCallback(async (file: InstructionAttachment) => {
    const result = await openProjectFileInNewTab(file);
    if (!result.ok) {
      setOpenError(result.error || FILE_OPEN_ERROR_MESSAGE);
      return;
    }
    setOpenError('');
  }, []);

  const imageFiles = useMemo(
    () => files.filter((f) => isImage(f.file_type)),
    [files]
  );

  const nonImageFiles = useMemo(
    () => files.filter((f) => !isImage(f.file_type)),
    [files]
  );

  const pdfFiles = useMemo(
    () => nonImageFiles.filter((f) => isPdf(f.file_type, f.file_name)),
    [nonImageFiles]
  );

  const otherNonImageFiles = useMemo(
    () => nonImageFiles.filter((f) => !isPdf(f.file_type, f.file_name)),
    [nonImageFiles]
  );

  const lightboxPhotos: LightboxPhoto[] = useMemo(
    () =>
      imageFiles.map((f) => ({
        url: fileUrls[f.id] || resolveFileDisplayUrl(f),
        caption: f.caption || f.file_name,
        tag: getPhotoTypeLabel(f.photo_type),
      })),
    [imageFiles, fileUrls]
  );

  const visibleImages = imageFiles.slice(0, maxVisible);
  const hiddenCount = imageFiles.length - maxVisible;

  if (files.length === 0) return null;

  const singleLayout = visibleImages.length === 1;

  return (
    <div className={className}>
      {openError && (
        <div className="mb-2 bg-[#FEF2F2] border border-[#FECACA] rounded-lg px-2.5 py-2 text-[#EF4444] text-[11px]">
          {openError}
        </div>
      )}

      {/* Image thumbnails */}
      {visibleImages.length > 0 && (
        <div
          className={`flex gap-2 ${singleLayout ? '' : 'flex-nowrap overflow-x-auto scrollbar-hide'}`}
        >
          {visibleImages.map((file, idx) => {
            const isLast = idx === visibleImages.length - 1;
            const hasOverlay = isLast && hiddenCount > 0;
            const displayUrl = fileUrls[file.id] || resolveFileDisplayUrl(file);

            return (
              <div
                key={file.id}
                className={`relative flex-shrink-0 overflow-hidden rounded-xl border border-gray-100 bg-gray-50 cursor-pointer group ${
                  singleLayout
                    ? 'w-full h-[120px] sm:h-[140px]'
                    : 'w-[96px] h-[96px]'
                }`}
                onClick={() => {
                  setLightboxIndex(idx);
                  setLightboxOpen(true);
                }}
              >
                {displayUrl ? (
                  <img
                    src={displayUrl}
                    alt={file.caption || file.file_name}
                    className="w-full h-full object-cover object-center transition-transform duration-200 group-hover:scale-105"
                    loading="lazy"
                    decoding="async"
                    onError={(e) => {
                      (e.target as HTMLImageElement).style.display = 'none';
                    }}
                  />
                ) : (
                  <div className="w-full h-full flex items-center justify-center text-[10px] text-[#9CA3AF] p-2 text-center">
                    Preview unavailable
                  </div>
                )}

                {(() => {
                  const badge = getChangeOrderVisibilityBadge(file) || getPhotoTypeLabel(file.photo_type);
                  if (!badge) return null;
                  return (
                    <div className="absolute top-1.5 left-1.5 bg-black/60 backdrop-blur-sm text-white text-[9px] font-bold px-1.5 py-0.5 rounded-md uppercase tracking-wide">
                      {badge}
                    </div>
                  );
                })()}

                {hasOverlay && (
                  <div className="absolute inset-0 bg-black/50 flex items-center justify-center">
                    <span className="text-white text-sm font-bold">
                      +{hiddenCount}
                    </span>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      {/* PDF cards */}
      {pdfFiles.length > 0 && (
        <div className={`space-y-2 ${visibleImages.length > 0 ? 'mt-2' : ''}`}>
          {pdfFiles.map((file) => (
            <div
              key={file.id}
              className="flex items-center gap-3 rounded-xl border border-gray-200 bg-white px-3 py-2.5"
            >
              <div className="w-10 h-10 rounded-lg bg-[#FEF2F2] flex items-center justify-center flex-shrink-0">
                <i className="ri-file-pdf-line text-[#EF4444] text-xl"></i>
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium text-[#1A1A1A] truncate">{file.file_name}</p>
                <p className="text-[10px] text-[#9CA3AF]">
                  PDF{getChangeOrderVisibilityBadge(file) ? ` · ${getChangeOrderVisibilityBadge(file)}` : ''}
                </p>
              </div>
              <div className="flex items-center gap-1 flex-shrink-0">
                <button
                  type="button"
                  onClick={() => void openAttachment(file)}
                  className="px-2.5 py-1.5 rounded-lg bg-[#F9FAFB] border border-gray-200 text-[#374151] text-[11px] font-semibold hover:border-[#E8621A]/30 transition-colors cursor-pointer"
                >
                  Open
                </button>
                <button
                  type="button"
                  onClick={() => void openAttachment(file)}
                  className="px-2.5 py-1.5 rounded-lg bg-[#FEF0E7] text-[#E8621A] text-[11px] font-semibold hover:bg-[#FDBA74]/30 transition-colors cursor-pointer"
                >
                  Download
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Other non-image file chips */}
      {otherNonImageFiles.length > 0 && (
        <div className="flex flex-wrap gap-2 mt-2">
          {otherNonImageFiles.map((file) => (
            <button
              key={file.id}
              type="button"
              onClick={() => void openAttachment(file)}
              className="inline-flex items-center gap-1.5 bg-[#F9FAFB] border border-gray-100 rounded-lg px-2.5 py-1.5 hover:border-[#E8621A]/30 transition-colors cursor-pointer"
            >
              <i className="ri-file-text-line text-[#9CA3AF] text-xs"></i>
              <span className="text-xs text-[#374151] font-medium truncate max-w-[140px]">
                {file.file_name}
              </span>
              <span className="text-[10px] text-[#9CA3AF] uppercase">
                {file.file_type}
              </span>
              {getChangeOrderVisibilityBadge(file) && (
                <span className="text-[10px] font-semibold text-[#374151]">
                  {getChangeOrderVisibilityBadge(file)}
                </span>
              )}
            </button>
          ))}
        </div>
      )}

      {lightboxOpen && lightboxPhotos.length > 0 && (
        <PhotoLightbox
          photos={lightboxPhotos}
          initialIndex={lightboxIndex}
          onClose={() => setLightboxOpen(false)}
        />
      )}
    </div>
  );
}
